/**
 * Coverage hook — Sprint 16: ShiftCoverage.
 * Manages coverage requests, candidate lists, and response actions.
 */

import { useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';

export interface CoverageRequest {
  id: number;
  requesting_member_id: number;
  duty_schedule_id: number;
  position_needed: string | null;
  status: string;
  filled_by_member_id: number | null;
  member_reason: string | null;
  officer_notes: string | null;
  outreach_mode: string;
  created_at: string;
  resolved_at: string | null;
  // Joined fields
  requesting_member_name?: string;
  shift_date?: string;
  shift_start?: string;
  shift_end?: string;
  shift_type?: string;
  battalion_name?: string;
  battalion_number?: number;
  candidate_count?: number;
  declined_count?: number;
  filled_by_name?: string;
}

export interface CoverageCandidate {
  id: number;
  request_id: number;
  member_id: number;
  rank: number;
  score: number;
  score_breakdown_json: string;
  ai_explanation: string | null;
  excluded_by_officer: number;
  exclusion_reason: string | null;
  notified_at: string | null;
  notification_channel: string | null;
  response: string | null;
  responded_at: string | null;
  // Joined fields
  display_name: string;
  email: string;
  battalion_name: string | null;
  battalion_number: number | null;
}

interface CoverageDetailResponse {
  request: CoverageRequest;
  shift: Record<string, unknown>;
  requesting_member: Record<string, unknown>;
  candidates: CoverageCandidate[];
  ai_ranking: unknown;
}

interface CoverageListResponse {
  data: CoverageRequest[];
  total: number;
}

export function useCoverage() {
  const [openRequests, setOpenRequests] = useState<CoverageRequest[]>([]);
  const [myRequests, setMyRequests] = useState<CoverageRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOpenRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<CoverageListResponse>('/api/coverage/open');
      setOpenRequests(res.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<CoverageListResponse>('/api/coverage/my-requests');
      setMyRequests(res.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRequestDetail = useCallback(async (requestId: number) => {
    const res = await apiFetch<CoverageDetailResponse>(`/api/coverage/request/${requestId}`);
    return res;
  }, []);

  const createRequest = useCallback(
    async (payload: {
      duty_schedule_id: number;
      position_needed?: string;
      member_reason?: string;
      on_behalf_of_member_id?: number;
    }) => {
      const res = await apiFetch<{ id: number; status: string; message: string }>('/api/coverage/request', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return res;
    },
    [],
  );

  const approveOutreach = useCallback(
    async (
      requestId: number,
      payload: {
        excluded_member_ids?: number[];
        exclusion_reasons?: Record<number, string>;
        outreach_mode?: string;
        sequential_timeout_minutes?: number;
        officer_notes?: string;
      },
    ) => {
      const res = await apiFetch<{ id: number; status: string; message: string }>(
        `/api/coverage/request/${requestId}/approve`,
        { method: 'POST', body: JSON.stringify(payload) },
      );
      return res;
    },
    [],
  );

  const respondToRequest = useCallback(async (candidateId: number, response: 'accepted' | 'declined') => {
    const res = await apiFetch<{ response: string; message: string }>(
      `/api/coverage/candidate/${candidateId}/respond`,
      { method: 'POST', body: JSON.stringify({ response }) },
    );
    return res;
  }, []);

  const cancelRequest = useCallback(async (requestId: number, reason?: string) => {
    const res = await apiFetch<{ id: number; status: string }>(`/api/coverage/request/${requestId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
    return res;
  }, []);

  const sendChatMessage = useCallback(
    async (requestId: number, message: string, history: Array<{ role: string; content: string }>) => {
      const res = await apiFetch<{ response: string; request_id: number }>('/api/coverage/chat', {
        method: 'POST',
        body: JSON.stringify({ message, request_id: requestId, history }),
      });
      return res;
    },
    [],
  );

  return {
    openRequests,
    myRequests,
    loading,
    error,
    fetchOpenRequests,
    fetchMyRequests,
    fetchRequestDetail,
    createRequest,
    approveOutreach,
    respondToRequest,
    cancelRequest,
    sendChatMessage,
  };
}
